import { useCallback, useState } from 'react'
import { useAudioRecorder } from './useAudioRecorder'
import { useSpeechRecognition, isSpeechRecognitionSupported } from './useSpeechRecognition'

/**
 * One capture session: microphone plus transcription.
 *
 * Both halves live in their own hook and fail on their own terms. This composes
 * them into the single object the capture UI actually wants, and decides what a
 * given pair of failures means for the parent:
 *
 *   full       both work — hold, talk, watch the words appear
 *   audio-only mic works, no transcription — hold and talk, correct it after
 *   none       no microphone at all — type it
 *
 * There is no fourth case where capture is impossible.
 */

export type CaptureSupport = 'full' | 'audio-only' | 'none'

export function detectSupport(): CaptureSupport {
  if (typeof window === 'undefined') return 'none'
  if (!navigator.mediaDevices?.getUserMedia) return 'none'
  return isSpeechRecognitionSupported() ? 'full' : 'audio-only'
}

export interface VoiceCapture {
  support: CaptureSupport
  recording: boolean
  /** Committed words so far. */
  transcript: string
  /** The phrase still being spoken. */
  interim: string
  /** Live amplitude, 0..1. Read inside an animation frame, never rendered. */
  getLevel: () => number
  getDurationMs: () => number
  /** Whichever failure the parent needs to know about, in their language. */
  error: string | null
  start: () => Promise<boolean>
  stop: () => Promise<{ transcript: string; blob: Blob | null; durationMs: number }>
  cancel: () => void
}

export function useVoiceCapture(): VoiceCapture {
  const [support] = useState<CaptureSupport>(detectSupport)
  const recorder = useAudioRecorder()
  const speech = useSpeechRecognition()
  const [recording, setRecording] = useState(false)

  const start = useCallback(async () => {
    const ok = await recorder.start()
    // Transcription only starts once the microphone is actually open, so a
    // denied permission produces one message rather than two.
    if (ok && speech.supported) speech.start()
    setRecording(ok)
    return ok
  }, [recorder, speech])

  const stop = useCallback(async () => {
    // Transcription first: it needs a moment to commit its last phrase, and
    // that moment overlaps the recorder flushing rather than following it.
    const [transcript, audio] = await Promise.all([
      speech.supported ? speech.stop() : Promise.resolve(''),
      recorder.stop(),
    ])
    setRecording(false)
    return { transcript, blob: audio.blob, durationMs: audio.durationMs }
  }, [recorder, speech])

  const cancel = useCallback(() => {
    recorder.cancel()
    speech.reset()
    setRecording(false)
  }, [recorder, speech])

  return {
    support,
    recording,
    transcript: speech.transcript,
    interim: speech.interim,
    getLevel: recorder.getLevel,
    getDurationMs: recorder.getDurationMs,
    // A dead microphone is the blocking problem; a dead transcriber is not.
    error: recorder.error ?? speech.error,
    start,
    stop,
    cancel,
  }
}
